import React, { useRef, useState } from 'react';
import api from '../lib/api';
import { Upload, Link as LinkIcon, X, Image as ImageIcon, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Resolve image URL: if it starts with /api/ -> prepend BACKEND_URL
export const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

const ImageUploader = ({ value, onChange, label = 'Immagine' }) => {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('url'); // 'url' or 'upload'

  const handleFile = async (file) => {
    setError('');
    if (!file) return;
    // Validate file
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      setError('Formato non supportato. Usa JPG, PNG, WebP o GIF.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('File troppo grande (max 5MB).');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/admin/upload-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(r.data.url);
    } catch (e) {
      setError(e.response?.data?.detail || 'Errore upload');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = () => onChange('');

  const display = resolveImageUrl(value);

  return (
    <div>
      <label className="block text-sm font-bold text-neutral-700 mb-1">{label}</label>
      {/* Preview */}
      {value && (
        <div className="mb-2 relative inline-block">
          <img src={display} alt="Anteprima" className="max-h-32 rounded border border-neutral-300 bg-neutral-50" />
          <button type="button" onClick={remove} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center hover:bg-red-700 shadow" aria-label="Rimuovi">
            <X size={14} />
          </button>
        </div>
      )}
      {!value && (
        <div className="mb-2 w-full max-h-32 h-24 border-2 border-dashed border-neutral-300 rounded flex items-center justify-center text-neutral-400">
          <ImageIcon size={28} />
        </div>
      )}
      {/* Tabs */}
      <div className="flex gap-1 mb-2 text-xs">
        <button type="button" onClick={() => setMode('url')} className={`px-3 py-1 rounded font-bold ${mode === 'url' ? 'bg-[#d62828] text-white' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'}`}>
          <LinkIcon size={12} className="inline mr-1" /> URL
        </button>
        <button type="button" onClick={() => setMode('upload')} className={`px-3 py-1 rounded font-bold ${mode === 'upload' ? 'bg-[#d62828] text-white' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'}`}>
          <Upload size={12} className="inline mr-1" /> Carica file
        </button>
      </div>
      {mode === 'url' && (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://..."
          className="w-full px-3 py-2 border border-neutral-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#d62828]/30"
        />
      )}
      {mode === 'upload' && (
        <div>
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-2 px-4 py-2 bg-[#d62828] text-white text-sm font-bold rounded hover:bg-[#b41f1f] disabled:opacity-60">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? 'Caricamento...' : (value ? 'Sostituisci' : 'Scegli file')}
          </button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
          <p className="text-xs text-neutral-500 mt-1">JPG, PNG, WebP, GIF · max 5MB</p>
        </div>
      )}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
};

export default ImageUploader;
